import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bold,
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  Eye,
  History,
  Italic,
  Link2,
  List,
  ListOrdered,
  Loader2,
  Mail,
  Pencil,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { Markdown } from "@/components/markdown";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input, Textarea } from "@/components/ui/input";
import { api, ApiError } from "@/lib/api";
import { copyRich, emailBodyHtml, emailBodyText, openLink, splitRecipients, type EmailDraft, type EmailFields } from "@/lib/email";
import { relativeTime } from "@/lib/format";
import type { AgentMessage } from "@/lib/types";
import { cn } from "@/lib/utils";

const NO_HANDLES = new Set<string>();

/**
 * An email an agent drafted (`email_draft`), drawn as a mail rather than a chat bubble:
 * To / Cc / Subject over the body as it will read. Edit turns every field into an
 * editor and Save keeps the version before; "Ask … to change it" sends the agent an
 * instruction, and it reissues the draft below. Copy writes rich HTML and plain text;
 * Open in Outlook fills a compose window. Nothing here sends.
 */
export function EmailDraftCard({
  agentId,
  agentName,
  message,
  email,
  now,
  canEdit,
  highlighted,
  onSaved,
}: {
  agentId: string;
  agentName: string;
  message: AgentMessage;
  email: EmailDraft;
  now: number;
  canEdit: boolean;
  highlighted?: boolean;
  /** The saved message, into the transcript at once rather than on the next push. */
  onSaved?: (message: AgentMessage) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const superseded = Boolean(email.supersededBy);

  return (
    <div
      data-email-draft={message.id}
      data-message-id={message.id}
      className={cn(
        "w-full max-w-[640px] overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow",
        highlighted && "ring-2 ring-primary",
        superseded && "opacity-70",
      )}
    >
      <div className="flex items-center gap-2 border-b border-border/70 bg-muted/40 px-3 py-1.5 text-[11.5px] text-muted-foreground">
        <Mail className="size-3.5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">
          Email draft{email.revisionOf ? " · revised" : ""}
          {email.revisions.length > 0 ? ` · edited ${relativeTime(email.revisions[email.revisions.length - 1]!.savedAt, now)}` : ""}
          {superseded ? " · replaced by a newer draft below" : ""}
        </span>
        {!editing && canEdit ? (
          <Button variant="ghost" size="xs" onClick={() => setEditing(true)}>
            <Pencil /> Edit
          </Button>
        ) : null}
      </div>

      {email.inReplyTo && !editing ? (
        <blockquote className="mx-3 mt-2.5 line-clamp-3 border-l-2 border-border pl-2.5 text-[12px] text-muted-foreground">{email.inReplyTo}</blockquote>
      ) : null}

      {editing ? (
        <EmailEditor
          agentId={agentId}
          messageId={message.id}
          email={email}
          onSaved={onSaved}
          onDone={() => setEditing(false)}
        />
      ) : (
        <>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 px-3 pt-2.5 text-[12.5px]">
            <Field label="To" value={email.to.join(", ") || "—"} />
            {email.cc.length > 0 ? <Field label="Cc" value={email.cc.join(", ")} /> : null}
            <Field label="Subject" value={email.subject} strong />
          </dl>
          <div className="mx-3 mt-2 border-t border-border/60 pb-3 pt-2.5 text-[13.5px] leading-relaxed">
            <Markdown text={email.body} handles={NO_HANDLES} />
          </div>
          <EmailActions email={email} />
          {canEdit && !superseded ? <ReviseBox agentId={agentId} agentName={agentName} messageId={message.id} /> : null}
        </>
      )}

      {email.revisions.length > 0 && !editing ? (
        <div className="border-t border-border/60 px-3 py-1.5">
          <button className="flex items-center gap-1 text-[11.5px] text-muted-foreground hover:text-foreground" onClick={() => setShowRevisions((open) => !open)}>
            <History className="size-3" /> {email.revisions.length} earlier version{email.revisions.length === 1 ? "" : "s"}
            <ChevronDown className={cn("size-3 transition-transform", showRevisions && "rotate-180")} />
          </button>
          {showRevisions ? (
            <ol className="mt-1.5 space-y-2">
              {[...email.revisions].reverse().map((revision) => (
                <li key={revision.savedAt} className="rounded-md bg-muted/40 px-2.5 py-2 text-[12px]">
                  <div className="mb-1 flex items-center gap-2 text-muted-foreground">
                    <span className="min-w-0 flex-1 truncate">
                      {revision.subject} · replaced {relativeTime(revision.savedAt, now)}
                    </span>
                    {canEdit ? <RestoreButton agentId={agentId} messageId={message.id} revision={revision} onSaved={onSaved} /> : null}
                  </div>
                  <p className="line-clamp-4 whitespace-pre-wrap text-muted-foreground">{emailBodyText(revision.body)}</p>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("min-w-0 break-words", strong && "font-semibold")}>{value}</dd>
    </>
  );
}

/** Copy, Copy subject, Open in Outlook (web) and in the mail app. */
function EmailActions({ email }: { email: EmailFields }) {
  const [copied, setCopied] = useState<"body" | "subject" | null>(null);
  const flash = (what: "body" | "subject") => {
    setCopied(what);
    setTimeout(() => setCopied((current) => (current === what ? null : current)), 1500);
  };
  const copyBody = async () => {
    try {
      await copyRich(emailBodyHtml(email.body), emailBodyText(email.body));
      flash("body");
    } catch {
      toast.error("Could not copy — select the text and copy it instead");
    }
  };
  const open = async (kind: "outlook" | "mailto") => {
    const { url, bodyLeftOut } = openLink(kind, email);
    if (bodyLeftOut) {
      // Too long for a link: the fields go in the link, the body on the clipboard.
      await copyRich(emailBodyHtml(email.body), emailBodyText(email.body)).catch(() => undefined);
      toast("The body is too long for the link, so it is on your clipboard — paste it into the compose window.");
    }
    if (kind === "outlook") window.open(url, "_blank", "noopener");
    else window.location.href = url;
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t border-border/60 px-3 py-2">
      <Button size="xs" onClick={() => void copyBody()}>
        {copied === "body" ? <Check /> : <Copy />} {copied === "body" ? "Copied" : "Copy"}
      </Button>
      <Button
        size="xs"
        variant="outline"
        onClick={() =>
          void navigator.clipboard.writeText(email.subject).then(
            () => flash("subject"),
            () => toast.error("Could not copy the subject"),
          )
        }
      >
        {copied === "subject" ? <Check /> : <Copy />} {copied === "subject" ? "Copied" : "Copy subject"}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="xs" variant="outline">
            <ExternalLink /> Open in Outlook <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => void open("outlook")}>Outlook on the web</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void open("mailto")}>Mail app (Outlook desktop / phone)</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** "Shorter", "more formal": goes to the agent, which reissues the draft below. */
function ReviseBox({ agentId, agentName, messageId }: { agentId: string; agentName: string; messageId: string }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    const instruction = text.trim();
    if (!instruction) return;
    setSending(true);
    try {
      await api.postAgentMessage(agentId, {
        body: `Change the email draft ${messageId}: ${instruction}\n\n(Reissue it with email_draft revises=${messageId}.)`,
        answers: false,
      });
      setText("");
      toast.success(`Asked ${agentName} to revise it`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not send");
    } finally {
      setSending(false);
    }
  };
  return (
    <form
      className="flex items-center gap-1.5 border-t border-border/60 px-3 py-2"
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      <Input
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={`Ask ${agentName} to change it — “shorter”, “more formal”…`}
        className="h-8 min-w-0 flex-1 text-[13px]"
        aria-label={`Ask ${agentName} to change the draft`}
      />
      <Button type="submit" size="icon-sm" variant="secondary" disabled={!text.trim() || sending} aria-label="Send the change">
        {sending ? <Loader2 className="animate-spin" /> : <Send />}
      </Button>
    </form>
  );
}

function RestoreButton({
  agentId,
  messageId,
  revision,
  onSaved,
}: {
  agentId: string;
  messageId: string;
  revision: EmailFields;
  onSaved?: (message: AgentMessage) => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="xs"
      variant="ghost"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          onSaved?.(await api.saveEmailDraft(agentId, messageId, revision));
          toast.success("Restored that version");
        } catch (error) {
          toast.error(error instanceof ApiError ? error.message : "Could not restore it");
        } finally {
          setBusy(false);
        }
      }}
    >
      Restore
    </Button>
  );
}

/** Wraps the selection in `before`/`after`, or inserts them around a placeholder. */
function wrapSelection(area: HTMLTextAreaElement, value: string, before: string, after: string, placeholder: string): { value: string; start: number; end: number } {
  const { selectionStart: start, selectionEnd: end } = area;
  const selected = value.slice(start, end) || placeholder;
  const next = value.slice(0, start) + before + selected + after + value.slice(end);
  return { value: next, start: start + before.length, end: start + before.length + selected.length };
}

/** Starts every selected line (or the current one) with `prefix`, numbering for "1. ". */
function prefixLines(area: HTMLTextAreaElement, value: string, prefix: string): { value: string; start: number; end: number } {
  const lineStart = value.lastIndexOf("\n", area.selectionStart - 1) + 1;
  const lineEnd = value.indexOf("\n", area.selectionEnd);
  const end = lineEnd === -1 ? value.length : lineEnd;
  const lines = value.slice(lineStart, end).split("\n");
  const block = lines.map((line, index) => (prefix === "1. " ? `${index + 1}. ` : prefix) + line.replace(/^(\s*)([-*+]|\d+[.)])\s+/, "$1")).join("\n");
  return { value: value.slice(0, lineStart) + block + value.slice(end), start: lineStart, end: lineStart + block.length };
}

function EmailEditor({
  agentId,
  messageId,
  email,
  onSaved,
  onDone,
}: {
  agentId: string;
  messageId: string;
  email: EmailDraft;
  onSaved?: (message: AgentMessage) => void;
  onDone: () => void;
}) {
  const [to, setTo] = useState(email.to.join(", "));
  const [cc, setCc] = useState(email.cc.join(", "));
  const [subject, setSubject] = useState(email.subject);
  const [body, setBody] = useState(email.body);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const pending = useRef<{ start: number; end: number } | null>(null);

  useEffect(() => {
    if (!pending.current || !area.current) return;
    area.current.focus();
    area.current.setSelectionRange(pending.current.start, pending.current.end);
    pending.current = null;
  }, [body]);

  const apply = (edit: (el: HTMLTextAreaElement, value: string) => { value: string; start: number; end: number }) => {
    if (!area.current) return;
    const next = edit(area.current, body);
    pending.current = { start: next.start, end: next.end };
    setBody(next.value);
  };
  const tools = useMemo(
    () => [
      { label: "Bold", icon: Bold, run: () => apply((el, v) => wrapSelection(el, v, "**", "**", "bold text")) },
      { label: "Italic", icon: Italic, run: () => apply((el, v) => wrapSelection(el, v, "_", "_", "italic text")) },
      { label: "Bulleted list", icon: List, run: () => apply((el, v) => prefixLines(el, v, "- ")) },
      { label: "Numbered list", icon: ListOrdered, run: () => apply((el, v) => prefixLines(el, v, "1. ")) },
      { label: "Link", icon: Link2, run: () => apply((el, v) => wrapSelection(el, v, "[", "](https://)", "link text")) },
    ],
    [body],
  );

  const save = async () => {
    setSaving(true);
    try {
      onSaved?.(await api.saveEmailDraft(agentId, messageId, { to: splitRecipients(to), cc: splitRecipients(cc), subject, body, inReplyTo: email.inReplyTo }));
      toast.success("Draft saved");
      onDone();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not save the draft");
    } finally {
      setSaving(false);
    }
  };

  const row = "grid grid-cols-[3.75rem_1fr] items-center gap-2";
  return (
    <div className="space-y-2 px-3 py-2.5">
      <label className={row}>
        <span className="text-[12px] text-muted-foreground">To</span>
        <Input value={to} onChange={(event) => setTo(event.target.value)} placeholder="name@example.com, …" className="h-8 text-[13px]" />
      </label>
      <label className={row}>
        <span className="text-[12px] text-muted-foreground">Cc</span>
        <Input value={cc} onChange={(event) => setCc(event.target.value)} className="h-8 text-[13px]" />
      </label>
      <label className={row}>
        <span className="text-[12px] text-muted-foreground">Subject</span>
        <Input value={subject} onChange={(event) => setSubject(event.target.value)} className="h-8 text-[13px] font-medium" />
      </label>
      <div className="overflow-hidden rounded-md border border-border">
        <div className="flex items-center gap-0.5 border-b border-border/70 bg-muted/40 px-1 py-0.5">
          {tools.map((tool) => (
            <Button key={tool.label} type="button" size="icon-sm" variant="ghost" aria-label={tool.label} title={tool.label} disabled={preview} onClick={tool.run}>
              <tool.icon />
            </Button>
          ))}
          <span className="flex-1" />
          <Button type="button" size="xs" variant={preview ? "secondary" : "ghost"} onClick={() => setPreview((on) => !on)}>
            <Eye /> {preview ? "Edit" : "Preview"}
          </Button>
        </div>
        {preview ? (
          <div className="min-h-48 px-3 py-2 text-[13.5px] leading-relaxed">
            <Markdown text={body} handles={NO_HANDLES} />
          </div>
        ) : (
          <Textarea
            ref={area}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") void save();
              if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b") {
                event.preventDefault();
                tools[0]!.run();
              }
              if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "i") {
                event.preventDefault();
                tools[1]!.run();
              }
            }}
            rows={12}
            className="min-h-48 resize-y rounded-none border-0 text-[13.5px] leading-relaxed shadow-none focus-visible:ring-0"
            aria-label="Body"
          />
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">Markdown: **bold**, _italic_, “- ” for a list, [text](link). Ctrl/⌘+Enter saves.</p>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => void save()} loading={saving} disabled={!subject.trim() || !body.trim()}>
          Save
        </Button>
      </div>
    </div>
  );
}
